import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Fade,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  Paper,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import { useDisagreementReviewSession } from '../../hooks/useDisagreementReviewSession';
import { evalRunForCriterion, useJudgementSource } from '../../hooks/useJudgementSource';
import { useNotification } from '../../hooks/useNotification';
import {
  fetchCriteria,
  fetchUserVsGtCandidates,
  fetchUserVsGtCounts,
  fetchUsers,
} from '../../services/api';
import { labelColors } from '../../theme';
import {
  type ConsensusMode,
  type Criteria,
  type DisagreementReviewOmissions,
  REVIEWED_CLASSIFICATIONS,
  type User,
  type UserVsGtCandidate,
  type UserVsGtCount,
} from '../../types';
import ClassificationChip from '../common/ClassificationChip';
import ConsensusModeToggle, { DEFAULT_CONSENSUS_MODE } from '../common/ConsensusModeToggle';
import JudgementSourceSelector from '../common/JudgementSourceSelector';
import LabelNav from '../common/LabelNav';
import ReviewFilterEmptyState from '../common/ReviewFilterEmptyState';
import { disagreementCandidatesEmptyCopy } from '../common/reviewEmptyState';
import SearchableSelect from '../common/SearchableSelect';
import TranscriptionNav from '../common/TranscriptionNav';
import EssayContent from '../EssayContent';

const UserVsGroundTruthReview: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [criteriaList, setCriteriaList] = useState<Criteria[]>([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedCriteriaId, setSelectedCriteriaId] = useState('');

  const [counts, setCounts] = useState<Record<string, UserVsGtCount>>({});
  const [omissions, setOmissions] = useState<DisagreementReviewOmissions>({
    unlinkedTranscriptions: 0,
    conflictingPapers: 0,
    missingJudgements: 0,
    unresolvedEntities: 0,
  });
  const [loadingCounts, setLoadingCounts] = useState(false);
  const [consensusMode, setConsensusMode] = useState<ConsensusMode>(DEFAULT_CONSENSUS_MODE);
  const [currentPolicyOnly, setCurrentPolicyOnly] = useState(true);
  const {
    source,
    setKind,
    evalRuns,
    productionFrom,
    productionTo,
    loadingEvalRuns,
    evalRunsError,
    usedProductionFallback,
    isReady,
  } = useJudgementSource();

  const { notification, setNotification, hideNotification } = useNotification(3000);

  const countsRequestIdRef = useRef(0);

  const usersById = useMemo(() => {
    const map: Record<string, User> = {};
    for (const u of users) map[u.id] = u;
    return map;
  }, [users]);

  const reviewerName = (userId: string) => {
    const u = usersById[userId];
    return u ? `${u.firstName} ${u.lastName}` : userId.slice(0, 8);
  };

  const userOptions = useMemo(
    () =>
      users
        .map(u => ({
          value: u.id,
          label: `${u.firstName} ${u.lastName} (${u.userType})`,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [users]
  );

  const criteriaOptions = useMemo(
    () =>
      criteriaList
        .filter(c => !c.isArchived || c.id === selectedCriteriaId)
        .map(c => {
          const count = counts[c.id];
          const countText = count
            ? `${count.unresolved}${count.unresolved !== count.total ? `/${count.total}` : ''}`
            : '0';
          return {
            value: c.id,
            label: c.name,
            unresolved: count?.unresolved ?? 0,
            total: count?.total ?? 0,
            renderContent: (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <Box component="span" sx={{ flex: 1 }}>
                  {c.name} [{c.granularity}]
                </Box>
                <Box component="span" sx={{ color: 'text.secondary' }}>
                  ({countText})
                </Box>
              </Box>
            ),
          };
        })
        .filter(o => o.total > 0)
        .sort((a, b) => {
          if (b.unresolved !== a.unresolved) return b.unresolved - a.unresolved;
          return a.label.localeCompare(b.label);
        })
        .map(({ value, label, renderContent }) => ({ value, label, renderContent })),
    [criteriaList, counts, selectedCriteriaId]
  );

  const reloadCounts = useCallback(() => {
    if (!selectedUserId || !isReady) {
      setCounts({});
      setLoadingCounts(false);
      return;
    }
    const requestId = ++countsRequestIdRef.current;
    setLoadingCounts(true);
    fetchUserVsGtCounts(selectedUserId, consensusMode, currentPolicyOnly, source)
      .then(({ counts: data, omissions: nextOmissions }) => {
        if (countsRequestIdRef.current !== requestId) return;
        const map: Record<string, UserVsGtCount> = {};
        for (const c of data) map[c.criteriaId] = c;
        setCounts(map);
        setOmissions(nextOmissions);
        setLoadingCounts(false);
      })
      .catch(() => {
        if (countsRequestIdRef.current !== requestId) return;
        setCounts({});
        setOmissions({
          unlinkedTranscriptions: 0,
          conflictingPapers: 0,
          missingJudgements: 0,
          unresolvedEntities: 0,
        });
        setLoadingCounts(false);
      });
  }, [selectedUserId, consensusMode, currentPolicyOnly, isReady, source]);

  useEffect(() => {
    fetchUsers().then(setUsers).catch(console.error);
    fetchCriteria().then(setCriteriaList).catch(console.error);
  }, []);

  useEffect(() => {
    setSelectedCriteriaId('');
  }, [selectedUserId]);

  useEffect(() => {
    reloadCounts();
  }, [reloadCounts]);

  const fetchCandidatesForSession = useCallback(
    (criteriaId: string) =>
      fetchUserVsGtCandidates(criteriaId, selectedUserId, consensusMode, currentPolicyOnly, source),
    [selectedUserId, consensusMode, currentPolicyOnly, source]
  );

  const session = useDisagreementReviewSession<UserVsGtCandidate>({
    selectedCriteriaId,
    criteriaList,
    enabled: Boolean(selectedUserId) && isReady,
    judgementSource: source,
    fetchCandidates: fetchCandidatesForSession,
    onCandidatesError: () =>
      setNotification({
        open: true,
        message:
          source.kind === 'eval'
            ? 'Could not load disagreement candidates for this Eval Run.'
            : 'Could not load disagreement candidates. Production judgements may be unavailable.',
        severity: 'error',
      }),
    onSubmitError: () =>
      setNotification({
        open: true,
        message: 'Failed to save review. Please try again.',
        severity: 'error',
      }),
    onSubmitSuccess: ({ wasUpdate }) =>
      setNotification({
        open: true,
        message: wasUpdate ? 'Review updated' : 'Review saved',
        severity: 'success',
      }),
    onUnresolvedCountChange: ({ criteriaId, delta }) => {
      setCounts(prev => {
        const existing = prev[criteriaId];
        if (!existing) return prev;
        return {
          ...prev,
          [criteriaId]: { ...existing, unresolved: existing.unresolved + delta },
        };
      });
    },
    getTranscriptionId: c => c.transcriptionId,
    getEntityText: c => c.sentence,
    getPaperNumber: c => c.paperNumber,
    getDisplayLabel: c => c.userLabel,
    getAlreadyReviewed: c => c.alreadyReviewed,
    getReview: c => c.review,
    getOriginalForSubmit: c => c.groundTruthLabel,
    applyOptimisticReview: (c, review) => ({ ...c, alreadyReviewed: true, review }),
  });

  const {
    selectedTranscriptionId,
    setSelectedTranscriptionId,
    loadingJudgement,
    activePolicy,
    loadingPolicy,
    editType,
    setEditType,
    editReason,
    setEditReason,
    submitting,
    transcriptionOptions,
    judgement,
    fragments,
    selectedCandidate,
    selectedFragmentIndex,
    unresolvedTotal,
    loadingCandidates,
    handleFragmentClick,
    getLabelTypeForFragment,
    isFragmentReviewed,
    handleSubmit,
    goToPreviousTranscription,
    goToNextTranscription,
    goToPreviousLabel,
    goToNextLabel,
    disagreementLabelPosition,
    disagreementLabelCount,
  } = session;

  const selectedGranularity =
    judgement?.granularity ??
    criteriaList.find(criterion => criterion.id === selectedCriteriaId)?.granularity ??
    'sentence';

  const emptyCopy = disagreementCandidatesEmptyCopy({
    currentPolicyOnly,
    consensusMode,
    includeAny: false,
    sourceKind: source.kind,
    evalRunCoversCriterion: Boolean(evalRunForCriterion(evalRuns, selectedCriteriaId || null)),
    comparedCount: counts[selectedCriteriaId]?.comparedCount ?? 0,
    scope: 'userCriterion',
  });

  const selectedUserName = selectedUserId
    ? (usersById[selectedUserId]?.firstName ?? '') +
      ' ' +
      (usersById[selectedUserId]?.lastName ?? '')
    : '';

  return (
    <Box
      sx={{ height: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column', mt: 4, px: 3 }}
    >
      <Typography variant="h4" sx={{ mb: 2 }}>
        Review user against ground truth
      </Typography>

      <Grid container spacing={2} sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <Grid item xs={12} md={5} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Paper className="paper">
            <Box className="header-box">
              <Typography variant="h6">Labelling Policy</Typography>
            </Box>
            <Box sx={{ px: 2, pt: 1, pb: 1 }}>
              <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                <InputLabel>User</InputLabel>
                <SearchableSelect
                  value={selectedUserId}
                  label="User"
                  onChange={e => setSelectedUserId(e.target.value as string)}
                  options={userOptions}
                />
              </FormControl>
              <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                <InputLabel>Criterion</InputLabel>
                <SearchableSelect
                  value={selectedCriteriaId}
                  label="Criterion"
                  onChange={e => setSelectedCriteriaId(e.target.value as string)}
                  disabled={!selectedUserId || loadingCounts}
                  options={criteriaOptions}
                />
                {selectedUserId && loadingCounts && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <CircularProgress size={14} />
                    <Typography variant="caption" color="text.secondary">
                      Loading disagreement counts for {selectedUserName.trim() || 'this user'}…
                    </Typography>
                  </Box>
                )}
              </FormControl>
              <JudgementSourceSelector
                source={source}
                evalRuns={evalRuns}
                productionFrom={productionFrom}
                productionTo={productionTo}
                loadingEvalRuns={loadingEvalRuns}
                evalRunsError={evalRunsError}
                usedProductionFallback={usedProductionFallback}
                selectedCriteriaId={selectedCriteriaId}
                onKindChange={setKind}
              />
              <ConsensusModeToggle value={consensusMode} onChange={setConsensusMode} />
              <Tooltip title="Only consider human labels created against each criterion's current policy. Labels written against an older policy version are excluded.">
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={currentPolicyOnly}
                      onChange={e => setCurrentPolicyOnly(e.target.checked)}
                    />
                  }
                  label="Current policy only"
                  sx={{ ml: 0 }}
                />
              </Tooltip>
              {(omissions.unlinkedTranscriptions > 0 ||
                omissions.conflictingPapers > 0 ||
                (omissions.missingJudgements ?? 0) > 0 ||
                (omissions.unresolvedEntities ?? 0) > 0) && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  Counts omit {omissions.unlinkedTranscriptions} unlinked transcription
                  {omissions.unlinkedTranscriptions === 1 ? '' : 's'}, {omissions.conflictingPapers}{' '}
                  paper
                  {omissions.conflictingPapers === 1 ? '' : 's'} with conflicting production links,
                  and {omissions.missingJudgements ?? 0} linked Criterion and Transcription pair
                  {(omissions.missingJudgements ?? 0) === 1 ? '' : 's'} without matching AI
                  Judgements, plus {omissions.unresolvedEntities ?? 0} unresolved{' '}
                  {(omissions.unresolvedEntities ?? 0) === 1 ? 'entity' : 'entities'}.
                </Alert>
              )}
            </Box>
            <Box className="content-box">
              <Box className="markdown-container">
                {loadingPolicy ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : activePolicy ? (
                  <ReactMarkdown remarkPlugins={[remarkBreaks]}>{activePolicy.text}</ReactMarkdown>
                ) : (
                  <Typography color="text.secondary" sx={{ pt: 2, textAlign: 'center' }}>
                    {selectedUserId && selectedCriteriaId
                      ? 'No policy found for this criterion'
                      : selectedUserId
                        ? 'Select a criterion to view its policy'
                        : 'Select a user to begin'}
                  </Typography>
                )}
              </Box>
            </Box>
          </Paper>
        </Grid>

        <Grid
          item
          xs={12}
          md={7}
          sx={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}
        >
          <Fade in={notification.open}>
            <Box
              sx={{
                position: 'absolute',
                top: 12,
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 10,
              }}
            >
              <Alert onClose={hideNotification} severity={notification.severity}>
                {notification.message}
              </Alert>
            </Box>
          </Fade>
          <Paper className="paper">
            <Box className="header-box">
              <Typography variant="h6">
                Transcription{' '}
                {selectedCriteriaId && selectedUserId && !loadingCandidates && (
                  <Typography component="span" variant="body2" color="text.secondary">
                    ({unresolvedTotal} unresolved across {transcriptionOptions.length} transcription
                    {transcriptionOptions.length === 1 ? '' : 's'})
                  </Typography>
                )}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Chip label="Positive" size="small" color="success" variant="outlined" />
                <Chip label="Negative" size="small" color="error" variant="outlined" />
                <Chip label="Uncertain" size="small" color="warning" variant="outlined" />
              </Box>
            </Box>

            {selectedCriteriaId && selectedUserId && (
              <Box sx={{ px: 2, pt: 1, pb: 1 }}>
                <TranscriptionNav
                  value={selectedTranscriptionId}
                  options={transcriptionOptions.map(o => ({ value: o.value, label: o.label }))}
                  disabled={loadingCandidates || transcriptionOptions.length === 0}
                  onChange={setSelectedTranscriptionId}
                  onPrevious={goToPreviousTranscription}
                  onNext={goToNextTranscription}
                />
                {disagreementLabelCount > 0 && (
                  <LabelNav
                    position={disagreementLabelPosition}
                    total={disagreementLabelCount}
                    onPrevious={goToPreviousLabel}
                    onNext={goToNextLabel}
                  />
                )}
                {judgement?.policyId && activePolicy && judgement.policyId !== activePolicy.id && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    AI judgement was made using an older policy version
                  </Alert>
                )}
                {judgement && (judgement.excludedEntityCount ?? 0) > 0 && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    {judgement.excludedEntityCount ?? 0}{' '}
                    {judgement.excludedEntityCount === 1 ? 'entity was' : 'entities were'} excluded
                    because they could not be resolved safely.
                  </Alert>
                )}
                {(judgement?.entityWarnings ?? []).map((warning, index) => (
                  <Alert key={`${warning}-${index}`} severity="warning" sx={{ mt: 1 }}>
                    {warning}
                  </Alert>
                ))}
              </Box>
            )}

            <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0, pl: 1 }}>
              {loadingCandidates || loadingJudgement ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : !isReady ? (
                <Typography color="text.secondary" sx={{ pt: 2, textAlign: 'center' }}>
                  Select an Eval Run to compare AI Judgements
                </Typography>
              ) : !selectedUserId ? (
                <Typography color="text.secondary" sx={{ pt: 2, textAlign: 'center' }}>
                  Select a user to begin
                </Typography>
              ) : !selectedCriteriaId ? (
                <Typography color="text.secondary" sx={{ pt: 2, textAlign: 'center' }}>
                  Select a criterion to begin
                </Typography>
              ) : transcriptionOptions.length === 0 ? (
                <ReviewFilterEmptyState title={emptyCopy.title} hint={emptyCopy.hint} />
              ) : fragments.length > 0 ? (
                <EssayContent
                  fragments={fragments}
                  selectedFragmentIndex={selectedFragmentIndex}
                  handleFragmentClick={handleFragmentClick}
                  getLabelTypeForFragment={getLabelTypeForFragment}
                  isFragmentReviewed={isFragmentReviewed}
                  granularity={selectedGranularity}
                />
              ) : (
                <Typography color="text.secondary" sx={{ pt: 2, textAlign: 'center' }}>
                  No {selectedGranularity} entities returned for this transcription
                </Typography>
              )}
            </Box>

            {selectedCandidate && (
              <Box
                sx={{
                  pt: 1,
                  mt: 1,
                  borderTop: '1px solid rgba(0, 0, 0, 0.12)',
                  maxHeight: '55%',
                  overflow: 'auto',
                  px: 1,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="subtitle2">{selectedUserName || 'User'}</Typography>
                  <ClassificationChip value={selectedCandidate.userLabel} />
                  <Typography variant="body2" color="text.secondary">
                    vs Ground truth ({selectedCandidate.groundTruthReviewerCount})
                  </Typography>
                  <ClassificationChip value={selectedCandidate.groundTruthLabel} />
                  {selectedCandidate.alreadyReviewed && (
                    <Chip label="Reviewed" size="small" color="primary" />
                  )}
                </Box>
                <Box
                  sx={{
                    p: 1,
                    mb: 1,
                    borderRadius: 1,
                    bgcolor: labelColors[selectedCandidate.userLabel].main,
                    border: `1px solid ${labelColors[selectedCandidate.userLabel].border}`,
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {selectedUserName || 'User'}:
                  </Typography>{' '}
                  <Typography variant="body2" component="span">
                    {selectedCandidate.userReason || '(no reason)'}
                  </Typography>
                </Box>
                {selectedCandidate.groundTruthReasons.map((r, idx) => (
                  <Box key={idx} sx={{ mb: 0.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      {reviewerName(r.userId)}:
                    </Typography>{' '}
                    <Typography variant="body2" component="span">
                      {r.reason || '(no reason)'}
                    </Typography>
                  </Box>
                ))}

                <Typography variant="subtitle2" sx={{ mb: 1, mt: 2 }}>
                  Correct classification
                </Typography>
                <ToggleButtonGroup
                  value={editType}
                  exclusive
                  onChange={(_e, val) => {
                    if (val !== null) setEditType(val);
                  }}
                  size="small"
                  sx={{ mb: 1.5, flexWrap: 'wrap' }}
                >
                  {REVIEWED_CLASSIFICATIONS.map(type => (
                    <ToggleButton
                      key={type}
                      value={type}
                      sx={{
                        '&.Mui-selected': {
                          bgcolor: labelColors[type].main,
                          borderColor: labelColors[type].border,
                        },
                      }}
                    >
                      {type}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>

                <TextField
                  fullWidth
                  size="small"
                  multiline
                  minRows={2}
                  maxRows={5}
                  placeholder="Reason for this call..."
                  value={editReason}
                  onChange={e => setEditReason(e.target.value)}
                  sx={{ mb: 1 }}
                />

                <Button
                  variant="contained"
                  size="small"
                  disabled={!editType || !editReason.trim() || !activePolicy || submitting}
                  onClick={handleSubmit}
                >
                  {selectedCandidate.alreadyReviewed ? 'Update review' : 'Submit review'}
                </Button>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default UserVsGroundTruthReview;
